import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { openPdf } from '../utils/pdfHelpers';

/**
 * Diálogo para generar el listado de stock en PDF acotado por categoría,
 * marca, mínimos o incluyendo lo que está en cero. Sin nada tildado sale el
 * listado completo (comportamiento histórico del botón).
 *
 * Lo usan el botón de Productos y Servicios y el de Stock de Productos.
 */

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Select con buscador y selección múltiple (categorías / marcas) */
function MultiSelect({ label, options, selected, setSelected, emptyText, placeholder }: {
  label: string; options: string[]; selected: string[];
  setSelected: (v: string[]) => void; emptyText: string; placeholder: string;
}) {
  const [abierto, setAbierto] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      <Popover open={abierto} onOpenChange={setAbierto}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
            <span className="truncate">
              {selected.length === 0 ? <span className="text-muted-foreground">{placeholder}</span>
                : selected.length <= 2 ? selected.join(', ')
                : `${selected.length} seleccionadas`}
            </span>
            <ChevronUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder="Buscar..." />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map(o => {
                  const activo = selected.includes(o);
                  return (
                    <CommandItem key={o} value={o} onSelect={() => setSelected(selected.includes(o) ? selected.filter(v => v !== o) : [...selected, o])} className="cursor-pointer">
                      <CheckIcon className={`mr-2 h-4 w-4 ${activo ? 'opacity-100' : 'opacity-0'}`} />
                      {o}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {selected.map(v => (
            <span key={v} className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs">
              {v}
              <button type="button" onClick={() => setSelected(selected.filter(x => x !== v))} className="text-muted-foreground hover:text-foreground">
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StockPdfDialog({ open, onOpenChange }: Props) {
  const [generating, setGenerating] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [onlyBelowMin, setOnlyBelowMin] = useState(false);
  const [includeZero, setIncludeZero] = useState(false);
  const [catOptions, setCatOptions] = useState<string[]>([]);
  const [brandOptions, setBrandOptions] = useState<string[]>([]);

  useEffect(() => {
    if (!open || catOptions.length || brandOptions.length) return;
    axios.get('/api/products/categories')
      .then(r => setCatOptions((r.data?.categories || []).filter((c: string) => c && c !== 'servicio')))
      .catch(() => {});
    axios.get('/api/products/distinct-meta')
      .then(r => {
        // La misma marca viene cargada con distintas mayúsculas (PIRELLI /
        // Pirelli). Se muestra una sola: el filtro compara sin distinguir.
        const porNombre = new Map<string, string>();
        for (const raw of (r.data?.brands || []) as string[]) {
          const key = String(raw).trim().toLowerCase();
          if (!key) continue;
          const actual = porNombre.get(key);
          const esTodoMayus = (v: string) => v === v.toUpperCase();
          if (!actual || (esTodoMayus(actual) && !esTodoMayus(raw))) porNombre.set(key, raw);
        }
        setBrandOptions(Array.from(porNombre.values()).sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => {});
  }, [open, catOptions.length, brandOptions.length]);

  const generate = async () => {
    try {
      setGenerating(true);
      const res = await axios.post('/api/products/generate-stock-pdf', {
        categories, brands, onlyBelowMin, includeZeroStock: includeZero,
      }, { responseType: 'blob', timeout: 60000 });
      if (!res.data || res.data.size === 0) throw new Error('PDF vacio');
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const result = openPdf({ blob, filename: `stock_${Date.now()}.pdf` });
      toast.success(result === 'downloaded' ? 'PDF descargado' : 'PDF generado');
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Error al generar PDF');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={o => { if (!generating) onOpenChange(o); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Listado de stock en PDF</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Sin nada tildado sale el listado completo. Tildá para acotarlo.
          </p>

          <MultiSelect
            label="Categorías"
            options={catOptions}
            selected={categories}
            setSelected={setCategories}
            placeholder="Todas las categorías"
            emptyText="Sin categorías"
          />

          <MultiSelect
            label="Marcas"
            options={brandOptions}
            selected={brands}
            setSelected={setBrands}
            placeholder="Todas las marcas"
            emptyText="Sin marcas"
          />

          <div className="space-y-2 rounded-lg border p-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" checked={onlyBelowMin} onChange={e => setOnlyBelowMin(e.target.checked)} className="h-4 w-4" />
              Solo lo que está en o por debajo del stock mínimo
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" checked={includeZero} onChange={e => setIncludeZero(e.target.checked)} className="h-4 w-4" />
              Incluir artículos sin stock (para reponer)
            </label>
          </div>

          {(categories.length > 0 || brands.length > 0) && (
            <button
              type="button"
              onClick={() => { setCategories([]); setBrands([]); }}
              className="text-xs text-muted-foreground underline hover:text-foreground"
            >
              Limpiar selección
            </button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={generating}>Cancelar</Button>
          <Button onClick={generate} disabled={generating}>{generating ? 'Generando...' : 'Generar PDF'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
